/**
 * PermissionGate — wraps content that requires specific permissions.
 * Used as a route guard in routes/index.tsx and inline in components.
 *
 * Renders children if the user has ANY of the required permissions.
 * Otherwise renders a "not authorised" message.
 */

import { useAuthStore } from '@/stores/auth'
import { Link } from 'react-router'
import { ShieldX } from 'lucide-react'
import { regionalCopy } from '@/lib/regional-copy'

interface PermissionGateProps {
  /** Permission string(s) — user needs at least one to pass */
  require: string | readonly string[]
  children: React.ReactNode
}

export function PermissionGate({ require, children }: PermissionGateProps) {
  const hasPermission = useAuthStore(s => s.hasPermission)

  const perms = Array.isArray(require) ? require : [require]
  const allowed = perms.some(p => hasPermission(p))

  if (allowed) return <>{children}</>

  return <NotAuthorised />
}

/**
 * Admits platform STAFF — superadmin or support (`isPlatformStaff`). Only for
 * a page the server also opens to support; everything else under
 * `/manage/platform/*` stays on `PermissionGate require={MANAGE_PLATFORM}`.
 */
export function PlatformStaffGate({ children }: { children: React.ReactNode }) {
  const allowed = useAuthStore(s => s.isPlatformStaff())
  if (allowed) return <>{children}</>
  return <NotAuthorised />
}

function NotAuthorised() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <ShieldX className="w-10 h-10 text-ink-faint mb-4" />
      <h2 className="text-lg font-semibold text-ink mb-1">Not {regionalCopy('authorised')}</h2>
      <p className="text-sm text-ink-faint mb-6">You don't have permission to access this page.</p>
      <Link to="/" className="text-sm font-medium link">
        Back to home
      </Link>
    </div>
  )
}
